import type { ExecutionJobService } from "../../execution/jobService";

import type { ExecutionJob } from "../../execution/jobTypes";

import type { DepartmentId as ExecutionDepartmentId } from "../../execution/types";

import type { PersistentExecutionQueue } from "./executionQueueDelegation";

import type { WorkPackage } from "./agentExecutionContract";

function toExecutionDepartment(
  department: WorkPackage["department"],
): ExecutionDepartmentId {
  switch (department) {
    case "ENGINEERING":
      return "technology";

    case "DATABASE":
      return "technology";

    case "INFRASTRUCTURE":
      return "it-operations";

    case "SECURITY":
      return "security";

    case "QA":
      return "technology";

    case "DOCUMENTATION":
      return "documentation-knowledge";

    case "PROJECT_MANAGEMENT":
      return "project-management";

    case "FINANCE":
      return "finance";

    case "OTHER":
      return "product";

    default: {
      const exhaustive: never = department;
      throw new Error(
        `Unsupported orchestration department: ${String(exhaustive)}`,
      );
    }
  }
}

export interface ExecutionQueueScope {
  organizationId: string;
  productId?: string;
  projectId?: string;
}

/**
 * Maps Cyan orchestration work onto the already-certified
 * execution_jobs authority.
 *
 * This is an adapter, not a second queue.
 */
export class ExecutionJobQueueAdapter implements PersistentExecutionQueue {
  constructor(
    private readonly jobs: ExecutionJobService,

    private readonly scope: ExecutionQueueScope,
  ) {}

  async enqueue(input: {
    executionId: string;
    workItemId: string;
    phaseId: string;
    department: string;
    agentRole: string;
    payload: unknown;
  }) {
    const work = input.payload as WorkPackage;

    const now = new Date().toISOString();

    const job: ExecutionJob = {
      id: input.executionId,

      organizationId: this.scope.organizationId,

      productId: this.scope.productId,

      projectId: this.scope.projectId,

      workItemId: input.workItemId,

      phaseId: input.phaseId,

      title: work.objective,

      objective: work.objective,

      sourceSpec: {
        path: "cyan://operational-takeover",
        approved: true,
      },

      department: toExecutionDepartment(work.department),

      risk: "GREEN",

      status: "QUEUED",

      assignedAgentId: undefined,

      attempt: 1,

      budget: {
        /*
         * B15 full-operational-autonomy bootstrap.
         *
         * Goal lifetime is controlled by durable state and verification,
         * not by the conversation lifetime or a ten-minute runtime limit.
         *
         * Repeated failures remain observable and must trigger a diagnostic
         * strategy change rather than terminating otherwise recoverable work.
         */
        maxAttempts: 500,
        maxRepeatedFailureCount: 50,
      },

      leaseOwner: undefined,

      leaseExpiresAt: undefined,

      lastCheckpointId: undefined,

      createdAt: now,

      updatedAt: now,
    };

    await this.jobs.enqueue(job);
  }
}
